import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { debounce } from "lodash";
import { ActiveCampaignAutomations } from "@hilos/types/private-schema";
import { useStepField } from "src/containers/flow/builder/hooks/useStepField";
import SelectorField from "src/components/Form/SelectorField";
import useActiveCampaignAppConfig from "src/hooks/useActiveCampaignAppConfig";
import { API_ROUTES } from "src/router/router";
import { buildAPIRoute } from "src/router/router";
import ActiveCampaignCTAInstallApp from "./ActiveCampaignCTAInstallApp";

interface ActiveCampaignAutomationOption {
  label: string;
  value: string;
  automation: ActiveCampaignAutomations;
}

interface FlowBuilderStepActiveCampaignAutomationProps {
  id: string;
  index: number;
}

function FlowBuilderStepActiveCampaignAutomation({
  id,
  index,
}: FlowBuilderStepActiveCampaignAutomationProps) {
  const [t] = useTranslation();
  const { data: activeCampaignAppConfig } = useActiveCampaignAppConfig();

  const [ACAutomationSelected, setACAutomationSelected] = useStepField({
    index,
    name: "ac_automation_selected",
  });
  const [ACAutomationAction] = useStepField({
    index,
    name: "ac_automation_action",
  });
  const [, setACAutomationName] = useStepField({
    index,
    name: "ac_automation_name",
  });

  const formatOptionLabel = ({ value, label, automation }) => {
    return (
      <div className="py-2">
        <div className="flex items-center justify-between">
          {automation && (
            <>
              <div>
                <h4 className="break-all font-medium">{label}</h4>
              </div>
            </>
          )}
        </div>
        <div className="truncate text-xs space-x-1">
          {automation &&
            automation.categories?.map((category) => (
              <span
                className="inline-flex items-center rounded-md bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-600 ring-1 ring-inset ring-indigo-500/10"
                key={category}
              >
                {category}
              </span>
            ))}
        </div>
      </div>
    );
  };

  const debouncedLoadOptions = debounce(
    (
      inputValue: string,
      callback: (options: ActiveCampaignAutomationOption[]) => void
    ) => {
      const params = inputValue ? { search: inputValue } : undefined;

      axios
        .get<ActiveCampaignAutomations[]>(
          buildAPIRoute(API_ROUTES.ACTIVECAMPAIGN_AUTOMATIONS),
          { params }
        )
        .then(({ data }) => {
          const options = data.map((automation: ActiveCampaignAutomations) => ({
            label: automation.name,
            value: String(automation.id),
            automation,
          }));
          callback(options);
        })
        .catch((error) => {
          console.error("Error loading automations:", error);
          callback([]);
        });
    },
    500
  );

  const loadOptions = debouncedLoadOptions;

  const handleACAutomationChange = useCallback(
    (option: ActiveCampaignAutomationOption) => {
      if (option?.automation) {
        setACAutomationSelected(option.automation);
        setACAutomationName(option.automation.name);
      }
    },
    [setACAutomationSelected, setACAutomationName]
  );

  const selectedOption = useMemo(() => {
    if (ACAutomationSelected) {
      return {
        label: ACAutomationSelected.name,
        value: String(ACAutomationSelected.id),
        automation: ACAutomationSelected,
      };
    }
    return null;
  }, [ACAutomationSelected]);

  return (
    <div className="space-y-2">
      {!activeCampaignAppConfig?.api_key_ro && (
        <div className="my-4">
          <ActiveCampaignCTAInstallApp />
        </div>
      )}
      <SelectorField
        label={
          ACAutomationAction === "START"
            ? t(
                "flows:steps.activecampaign.automation-start.label",
                "ActiveCampaign automation to start"
              )
            : t(
                "flows:steps.activecampaign.automation-end.label",
                "ActiveCampaign automation to end"
              )
        }
        name={`steps.${index}.ac_automation_id`}
        options={selectedOption ? [selectedOption] : []}
        loadOptions={loadOptions}
        defaultOptions={true}
        //@ts-ignore
        formatOptionLabel={formatOptionLabel}
        onSelect={(option) => {
          //@ts-ignore
          handleACAutomationChange(option);
        }}
      />

      {ACAutomationSelected && (
        <img
          src={ACAutomationSelected.screenshot}
          alt="screenshot"
          className="w-full"
        />
      )}
    </div>
  );
}

export default FlowBuilderStepActiveCampaignAutomation;
